import { MaintenanceCategory } from '@cobliteam/common-types';
import { useTranslation } from '@cobliteam/i18n';
import Button from '@cobliteam/ui/components/Button';
import Dropdown from '@cobliteam/ui/components/Dropdown';
import Checkbox from '@cobliteam/ui/form/Checkbox';
import Label from '@cobliteam/ui/form/Label';
import FilterIcon from '@cobliteam/ui-business/icons/filters/filter_alt_cb.svg?react';
import { useCallback, useEffect, useState } from 'react';

export interface MultiSelectMaintenanceCategoryProps {
  isOpen?: boolean;
  size?: 'sm' | 'md';
  selectedOptions?: string[];
  onChange: (selectedOptions: string[]) => void;
}

const MultiSelectMaintenanceCategory = ({ isOpen, size = 'md', selectedOptions = [], onChange }: MultiSelectMaintenanceCategoryProps) => {
  const { t } = useTranslation('multiselects');
  const [forceClosed, setForceClosed] = useState(false);
  const [filter, setFilter] = useState(selectedOptions || []);

  const getMaintenanceCategoryLabel = (category: MaintenanceCategory) => {
    switch (category) {
      case MaintenanceCategory.CORRECTIVE:
        return t('maintenance_category.corrective');
      case MaintenanceCategory.PREVENTIVE:
        return t('maintenance_category.preventive');
    }
  };

  const maintenanceCategorys = Object.values(MaintenanceCategory);

  // filter search
  const filterOptions = maintenanceCategorys.map((type: MaintenanceCategory) => ({
    key: type,
    label: getMaintenanceCategoryLabel(type),
  }));

  // check if all selected types are in filter
  const hasSameElements = selectedOptions?.length === filter?.length && selectedOptions.every((value) => filter.find((d: string) => d === value));

  const onClick = useCallback(() => {
    setFilter(selectedOptions || []);
  }, [selectedOptions]);

  const onChangeSelect = (type: string) => {
    const isSelected = filter.find((d: string) => d === type);
    const updatedSelected = isSelected ? filter.filter((d: string) => d !== type) : [...filter, type];
    setFilter(updatedSelected as MaintenanceCategory[]);
  };

  const handleClose = useCallback(() => {
    setForceClosed(true);
    setFilter([]);
  }, []);

  const handleApply = useCallback(() => {
    onChange(filter);
    handleClose();
  }, [filter, handleClose, onChange]);

  useEffect(() => setForceClosed(!isOpen), [isOpen]);

  useEffect(() => {
    if (forceClosed) setForceClosed(false);
  }, [forceClosed]);

  return (
    <Dropdown
      portal={false}
      onOpenChange={onClick}
      forceOpen={isOpen}
      forceClose={forceClosed}
      overlay={
        <Dropdown.Overlay>
          <Dropdown.Header className='px-2'>
            <Button variant='ghost' size='sm' disabled={filter?.length === 0} onClick={() => setFilter([])}>
              {t('maintenance_category.clear_selection')}
            </Button>
          </Dropdown.Header>
          <Dropdown.Body className='px-2'>
            <Dropdown.List
              heightList={300}
              heightItem={40}
              data={filterOptions || []}
              loadingComponent={<Dropdown.Loading />}
              itemComponent={(item) => (
                <div className='flex items-center gap-2'>
                  <Checkbox id={item.key} key={item.key} checked={filter.some((d) => d === item.key)} onCheckedChange={() => onChangeSelect(item.key)} />
                  <Label htmlFor={item.key}>{item.label}</Label>
                </div>
              )}
            />
          </Dropdown.Body>
          <Dropdown.Footer>
            <Dropdown.Actions className='px-2'>
              <Button full variant='ghost' size='sm' onClick={handleClose}>
                {t('maintenance_category.cancel')}
              </Button>
              <Button full variant='contained' size='sm' color='primary' onClick={handleApply} disabled={hasSameElements}>
                {t('maintenance_category.apply')}
              </Button>
            </Dropdown.Actions>
          </Dropdown.Footer>
        </Dropdown.Overlay>
      }
    >
      <Button size={size} variant='ghost' color='primary' startIcon={<FilterIcon />}>
        {t('maintenance_category.title')} {(selectedOptions?.length || 0) > 0 ? `(${selectedOptions?.length})` : ''}
      </Button>
    </Dropdown>
  );
};

export default MultiSelectMaintenanceCategory;
